import { memo } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    warningButton: {
        color: theme.palette.error.contrastText,
        backgroundColor: theme.palette.error.main,

        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
}));

/**
 * A consistent action button component for dialog actions
 *
 * Provides standard styling for primary and warning (destructive) actions.
 * Handles disabled state and forwards additional props to Material-UI Button.
 *
 * @component
 * @example
 * // Primary action button
 * <DialogActionButton
 *   text="Confirm"
 *   onClick={handleConfirm}
 *   disabled={isLoading}
 * />
 *
 * @example
 * // Warning/destructive action button
 * <DialogActionButton
 *   text="Delete"
 *   onClick={handleDelete}
 *   isWarning
 * />
 */
const DialogActionButton = memo(({ isWarning, disabled, onClick, className, text, buttonProps }) => {
    const classes = useStyles();

    if (isWarning) {
        return (
            <Button
                disabled={disabled}
                className={className || classes.warningButton}
                onClick={onClick}
                {...(buttonProps || {})}
            >
                {text}
            </Button>
        );
    }

    return (
        <Button variant="contained" color="primary" disabled={disabled} onClick={onClick} {...(buttonProps || {})}>
            {text}
        </Button>
    );
});

DialogActionButton.displayName = 'DialogActionButton';

DialogActionButton.propTypes = {
    /** If true, applies warning (error) styling for destructive actions */
    isWarning: PropTypes.bool,
    /** If true, button is disabled and cannot be clicked */
    disabled: PropTypes.bool,
    /** Callback fired when button is clicked */
    onClick: PropTypes.func.isRequired,
    /** Optional CSS class name to override default warning styling */
    className: PropTypes.string,
    /** Button text or element to display */
    text: PropTypes.node.isRequired,
    /** Additional props to pass to the underlying Material-UI Button */
    buttonProps: PropTypes.object,
};

DialogActionButton.defaultProps = {
    isWarning: false,
    disabled: false,
    className: null,
    buttonProps: null,
};

export default DialogActionButton;
